import { useMemo } from 'react'

import { summarizeObjects } from 'utils/object'
import { useApiGetSummaryPenilaianTahunanDinas } from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import { useApiGetSummaryPenilaianTahunanDinasTabGuru } from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'
import { DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED } from 'app/KinerjaDinasPage/constants'

import type { TResponsePenilaianSummaryData } from 'types/kinerja-dinas'

type Props = {
  year: number
}

const usePenetapanTahunanSummaryDinas = ({ year }: Props) => {
  // Calculations for Kepala Sekolah
  const {
    isLoading: isSummaryLoadingKs,
    isError: isSummaryErrorKs,
    isSuccess: isSummarySuccessKs,
    isFetched: isSummaryFetchedKs,
    data: { data: summaryKs } = {},
    refetch: refetchSummaryKs,
  } = useApiGetSummaryPenilaianTahunanDinas({ year: year }, { enabled: true })

  // Calculations for Guru
  const {
    isLoading: isSummaryLoadingGuru,
    isError: isSummaryErrorGuru,
    isSuccess: isSummarySuccessGuru,
    isFetched: isSummaryFetchedGuru,
    data: { data: summaryGuru } = {},
    refetch: refetchSummaryGuru,
  } = useApiGetSummaryPenilaianTahunanDinasTabGuru(
    { year: year },
    { enabled: DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED }
  )

  const isSummaryLoading = isSummaryLoadingKs || isSummaryLoadingGuru
  const isSummaryError = isSummaryErrorKs || isSummaryErrorGuru
  const isSummarySuccess = isSummarySuccessKs || isSummarySuccessGuru
  const isSummaryFetched = isSummaryFetchedKs || isSummaryFetchedGuru
  const isSummaryBothFetched = isSummaryFetchedKs && isSummaryFetchedGuru

  const summary: TResponsePenilaianSummaryData = useMemo(() => {
    if (!isSummaryLoading && isSummaryBothFetched)
      return summarizeObjects(
        {
          ...summaryKs,
          belumDikirim:
            summaryKs?.belumDikirim >= 0 ? summaryKs.belumDikirim : 0,
        },
        {
          ...summaryGuru,
          belumDikirim:
            summaryGuru?.belumDikirim >= 0 ? summaryGuru.belumDikirim : 0,
        }
      )

    return {} as TResponsePenilaianSummaryData
  }, [isSummaryLoading, isSummaryBothFetched, summaryKs, summaryGuru])

  return {
    // Kepala Sekolah
    isSummaryLoadingKs,
    isSummaryErrorKs,
    isSummarySuccessKs,
    summaryKs,
    refetchSummaryKs,

    // Guru
    isSummaryLoadingGuru,
    isSummaryErrorGuru,
    isSummarySuccessGuru,
    summaryGuru,
    refetchSummaryGuru,

    // Combined
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    isSuccess: isSummarySuccess,
    isFetched: isSummaryFetched,
    summary,
    refetch: async () => {
      await refetchSummaryKs()
      await refetchSummaryGuru()
    },
  }
}

export default usePenetapanTahunanSummaryDinas
